/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import AdvancedTableWrapper from "@civicplus/preamble-ui/lib/AdvancedTable/AdvancedTableWrapper";
import Button from "@civicplus/preamble-ui/lib/Button";
import Dialog from "@civicplus/preamble-ui/lib/Dialog";
import Grid from "@civicplus/preamble-ui/lib/Grid";
import Table from "@civicplus/preamble-ui/lib/Table";
import TableBody from "@civicplus/preamble-ui/lib/TableBody";
import TableCell from "@civicplus/preamble-ui/lib/TableCell";
import TableRow from "@civicplus/preamble-ui/lib/TableRow";
import Typography from "@civicplus/preamble-ui/lib/Typography";
import { bottle } from "../../../../provider/Bottle";
import { fromField } from "./shared";
import { format } from "date-fns";
import { getUsersName } from "@civicplus/preamble-ui/lib/Utilities/StringHelper";
import { MessageHistory } from "../../../../entities/Message";
import { MessageService } from "../../../../services/MessageService";
import { parseISO } from "date-fns/fp";
import {
    TableColumn,
    TableData,
    TableState
} from "@civicplus/preamble-ui/lib/AdvancedTable/AdvancedTableWrapperConstants";
import { IEmailActivityDetails, IMinimalBounceDetails } from "../../../../util/EmailMessageActivityDataTypes";

interface IMessageHistoryEmailActivityProps {
    listId: number | null;
    messageId: number;
    messageHistory: MessageHistory;
}

const MessageHistoryEmailActivity: React.FC<IMessageHistoryEmailActivityProps> = ({
    listId,
    messageId,
    messageHistory
}) => {
    const messageService: MessageService = bottle.container.MessageService;
    const [messageDetails, setMessageDetails] = useState<IEmailActivityDetails[] | undefined>(undefined);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [currentEventType, setCurrentEventType] = useState<string>("");
    const [currentRecipientEmail, setCurrentRecipientEmail] = useState<string>("");
    const [bounceEventDetails, setBounceEventDetails] = useState<IMinimalBounceDetails | undefined>(undefined);

    const showEmailActivityDialog = async (id: string, eventType: string, recipientEmail: string) => {
        const details: IEmailActivityDetails[] = await messageService.getMessageActivityDetails(
            messageId,
            id,
            eventType
        );
        setMessageDetails(details);
        setCurrentRecipientEmail(recipientEmail);
        setCurrentEventType(eventType);
        setDialogOpen(true);
    };

    useEffect(() => {
        const bounceDetails = messageDetails?.find((item) => item.type === "email_bounced")?.bounceDetails;
        if (bounceDetails && currentRecipientEmail && bounceEventDetails?.recipientEmail !== currentRecipientEmail) {
            setBounceEventDetails({
                bounceType: bounceDetails.bounceType,
                diagnosticCode:
                    (currentRecipientEmail &&
                        bounceDetails.bouncedRecipients.find(
                            (recipient) => recipient.emailAddress === currentRecipientEmail
                        )?.diagnosticCode) ??
                    "N/A",
                recipientEmail: currentRecipientEmail
            });
        }
    }, [bounceEventDetails, currentRecipientEmail, messageDetails]);

    const getEmailActivityDetails = () => {
        let to = "";
        let from = "";
        let bouncedItem: IEmailActivityDetails | undefined = undefined;

        const rows = messageDetails?.map((item: IEmailActivityDetails) => {
            if (item.type === "email_bounced") {
                bouncedItem = item;
            }

            if (item.to) {
                to = item.to;
            }

            if (item.metaData && item.metaData.from) {
                const replyTo = item.metaData.replyTo ? item.metaData.replyTo : "noreply@civicplus.com";
                from = `${item.metaData.from} <${replyTo}>`;
            }

            return (
                <TableRow id={`${item.from}-${item.time}`} key={`${item.from}-${item.time}`}>
                    <TableCell scope="row" id={`${item.from}-${item.time}-date`}>
                        {format(
                            new Date(parseISO(item.time.endsWith("Z") ? item.time : `${item.time}Z`)),
                            "MM/dd/yyyy h:mm aaaa"
                        )}
                    </TableCell>

                    <TableCell id={`${item.from}-${item.time}-event`}>
                        <Typography variant="button">{item.type.replace("email_", "")}</Typography>
                    </TableCell>
                </TableRow>
            );
        });

        // Show Some info first. Like Email address and message info?
        return (
            <>
                <Grid xs={12}>
                    <Grid container={true}>
                        <Grid xs={12} sm={3} md={2}>
                            <Typography paragraph={true}>Sender Name:</Typography>
                        </Grid>

                        <Grid xs={12} sm={9} md={10}>
                            <Typography color="textSecondary">{getUsersName(messageHistory!.sender!)}</Typography>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid xs={12}>
                    <Grid container={true}>
                        <Grid xs={12} sm={3} md={2}>
                            <Typography paragraph={true}>Subject:</Typography>
                        </Grid>

                        <Grid xs={12} sm={9} md={10}>
                            <Typography color="textSecondary" data-testid="subjectValue">
                                {messageHistory!.subject!}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>

                {fromField(messageHistory!, from)}

                <Grid xs={12}>
                    <Grid container={true}>
                        <Grid xs={12} sm={3} md={2}>
                            <Typography paragraph={true}>To:</Typography>
                        </Grid>

                        <Grid xs={12} sm={9} md={10}>
                            <Typography color="textSecondary">{to}</Typography>
                        </Grid>
                    </Grid>
                </Grid>

                {currentEventType === "email_bounced" && bounceEventDetails ? (
                    <>
                        <Grid xs={12}>
                            <Grid container={true}>
                                <Grid xs={12} sm={3} md={2}>
                                    <Typography paragraph={true}>Bounce Type:</Typography>
                                </Grid>

                                <Grid xs={12} sm={9} md={10}>
                                    <Typography color="textSecondary">{bounceEventDetails.bounceType}</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid xs={12}>
                            <Grid container={true}>
                                <Grid xs={12} sm={3} md={2}>
                                    <Typography paragraph={true}>Diagnostic Code:</Typography>
                                </Grid>

                                <Grid xs={12} sm={9} md={10}>
                                    <Typography color="textSecondary">{bounceEventDetails.diagnosticCode}</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                ) : null}

                <Grid xs={12}>
                    <Grid container={true}>
                        <Grid xs={12}>
                            <Table compact={true} id="activity-details">
                                <TableBody id="activity-details-body">{rows}</TableBody>
                            </Table>
                        </Grid>
                    </Grid>
                </Grid>
            </>
        );
    };

    const getEmailActivityRows = async (state: TableState): Promise<TableData> => {
        const activity = await messageService.getSentMessageActivity(messageId, listId, state.page, state.rowsPerPage);

        const items = activity.items.map((item: IEmailActivityDetails) => {
            return [
                <Typography variant="button">{item.type.replace("email_", "")}</Typography>,
                item.to ?? "",
                format(
                    new Date(parseISO(item.time.endsWith("Z") ? item.time : `${item.time}Z`)),
                    "MM/dd/yyyy h:mm aaaa"
                ),
                item.messageId,
                item.type
            ];
        });

        return { count: activity.count, data: items };
    };

    const columns: TableColumn[] = [
        { name: "event", label: "Event" },
        {
            name: "recipient",
            options: {
                label: "Recipient",
                filter: false
            }
        },
        {
            name: "date",
            label: "Date",
            options: {
                sort: true,
                filter: false
            }
        },
        {
            name: "messageId",
            label: "Message Id",
            options: { sort: false, filter: false, display: "false" }
        },
        {
            name: "emailEventType",
            label: "Email Event Type",
            options: { sort: false, filter: false, display: "false" }
        }
    ];

    return (
        <>
            <Dialog
                title="Message Log Details"
                fullWidth={true}
                maxWidth="xl"
                open={dialogOpen}
                data-testid="email-activity-dialog"
                onClose={() => {
                    setDialogOpen(false);
                    setMessageDetails(undefined);
                }}
                actions={[
                    <Button
                        color="primary"
                        onClick={async () => {
                            setDialogOpen(false);
                            setMessageDetails(undefined);
                        }}
                        key="ok"
                    >
                        OK
                    </Button>
                ]}
            >
                {messageDetails && getEmailActivityDetails()}
            </Dialog>

            <AdvancedTableWrapper
                columns={columns}
                rows={getEmailActivityRows}
                scrollToTop={true}
                emptyMessage="There are currently no events to view."
                download={false}
                initialSortDirection="desc"
                initialSortColumn="date"
                initialSortColumnIndex={2}
                showFilter={false}
                rowsPerPage={25}
                rowsPerPageOptions={[25, 50, 100]}
                onRowClick={async (rowData: string[], rowMeta: unknown) => {
                    const recipientEmail = rowData[1];
                    const id = rowData[3];
                    const eventType = rowData[4];
                    await showEmailActivityDialog(id, eventType, recipientEmail);
                }}
            />
        </>
    );
};
export default MessageHistoryEmailActivity;
